<script lang="ts">
	import classNames from 'classnames';
	import dayjs from 'dayjs';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import type { LatestPodcastEpisode } from '#lib/features/news/utils/getLatestPodcastEpisodes/getLatestPodcastEpisodes.js';
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { page } from '$app/state';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { HeniLogoMinimal } from '$global/assets/logos/HeniLogoMinimal';
	import { LinkButton } from '$global/components/LinkButton';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import type { NewsDataPageData } from '$routes/news/data/types';

	interface Props {
		showGlobalNav?: boolean;
	}

	let { showGlobalNav }: Props = $props();

	const data = getPageData<NewsDataPageData>(page.data);
	const latestPodcastEpisodes: LatestPodcastEpisode[] =
		data.latestPodcastEpisodes || [];
</script>

<div class="flex h-full flex-col">
	<div class="mt-[-4px] flex items-center gap-4 pb-6">
		<HeniLogoMinimal color="gray-900" class="h-[14px] w-[28px]" />
		<Txt variant="h5">Latest Podcasts</Txt>
	</div>

	<div class="flex-1 overflow-y-scroll">
		{#each latestPodcastEpisodes as episode, i}
			{@const date = dayjs(episode.publication_date).format('DD MMM, YYYY')}

			{#if i > 0}
				<hr />
			{/if}

			<a
				target="_blank"
				class={classNames('mb-4 flex items-center gap-3', { 'mt-4': i })}
				href={`${TalksRoutes.Podcasts}/${episode.podcastSlug}/episodes/${episode.slug}${showGlobalNav ? '' : '?global_nav=0'}`}
			>
				{#if episode.imageFilename}
					<img
						class="size-[48px] shrink-0 rounded object-cover"
						src={getImageUrl(episode.imageFilename, '256px')}
						alt=""
						loading="lazy"
					/>
				{/if}

				<!-- Kept to the thumbnail's 48px so the row reads as one block: the
				series and date lines are ~14px each, leaving the title a single
				clamped line rather than the two it would otherwise take. -->
				<div class="flex min-w-0 flex-1 flex-col justify-center">
					<div
						class="truncate text-[10px] leading-[14px] text-gray-500 uppercase"
					>
						{episode.podcastTitle}
					</div>
					<Txt class="line-clamp-1 leading-[20px]" variant="h6"
						>{episode.title}</Txt
					>
					<div class="truncate text-[10px] leading-[14px] text-gray-500">
						{date}
					</div>
				</div>
			</a>
		{/each}
	</div>

	<div class="flex h-[72px] items-center justify-center">
		<LinkButton
			size="md"
			variant="secondary"
			dataCy="news-data-view-podcasts"
			newTab
			href={`${GlobalRoutes.PodcastsHome}${showGlobalNav ? '' : '?global_nav=0'}`}
			>view podcasts
			{#snippet trailing()}
				<ChevronRightIcon />
			{/snippet}
		</LinkButton>
	</div>
</div>
